import DOMPurify from 'dompurify'
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Fonction debounce pour optimiser les performances
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

// Cache simple pour éviter les requêtes répétées
export class SimpleCache {
  private cache = new Map<string, { value: any; timestamp: number }>();
  private ttl: number;

  constructor(ttlMinutes: number = 5) {
    this.ttl = ttlMinutes * 60 * 1000; // Convert to milliseconds
  }

  set(key: string, value: any): void {
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }

  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return item.value;
  }

  clear(): void {
    this.cache.clear();
  }
}

// Sanitize any HTML before injecting into the DOM
export function sanitizeHTML(input: string) {
  return DOMPurify.sanitize(input, { USE_PROFILES: { html: true } });
}

// Build form URL with user data parameters (supports both Typeform and Airtable)
export function buildFormUrlWithUserData(
  baseUrl: string,
  userData: {
    first_name?: string;
    last_name?: string;
    email?: string;
    user_id?: string;
  },
  additionalData?: {
    module?: string;
    course?: string;
    module_id?: string;
    [key: string]: any;
  }
): string {
  if (!userData.user_id) return baseUrl;
  
  const url = new URL(baseUrl);
  const params = new URLSearchParams();
  
  // User data parameters
  if (userData.first_name) params.set('first_name', userData.first_name);
  if (userData.last_name) params.set('last_name', userData.last_name);
  if (userData.email) params.set('email', userData.email);
  if (userData.user_id) params.set('user_id', userData.user_id);
  
  // Additional data for Airtable prefill and hide
  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      if (value) {
        // For Airtable forms, add prefill_ prefix and hide parameter
        if (baseUrl.includes('airtable.com')) {
          params.set(`prefill_${encodeURIComponent(key)}`, encodeURIComponent(value));
          params.set(`hide_${encodeURIComponent(key)}`, 'true');
        } else {
          // For other forms (Typeform), use direct parameter
          params.set(key, value);
        }
      }
    });
    
    // Special handling for UUID_Apprenant field in Airtable
    if (baseUrl.includes('airtable.com') && userData.user_id) {
      params.set('prefill_UUID%20Apprenant', encodeURIComponent(userData.user_id));
      params.set('hide_UUID%20Apprenant', 'true');
    }
  }
  
  // Append parameters to the URL
  const paramString = params.toString();
  if (paramString) {
    url.search = paramString;
  }
  
  return url.toString();
}

// Legacy function for backward compatibility
export function buildTypeformUrlWithUserData(
  baseUrl: string,
  userData: {
    first_name?: string;
    last_name?: string;
    email?: string;
    user_id?: string;
  }
): string {
  return buildFormUrlWithUserData(baseUrl, userData);
}
